import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Cms, PAGE_TYPES } from '@impact/data';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import SwiperCore, { Lazy, Navigation, Scrollbar, Swiper } from 'swiper/core';
import { LazyOptions } from 'swiper/types';
import { ContentService } from '../content/content.service';
import { cloneObjectArray, removeDuplicatesById } from '../core/helpers';
import { SidePanelService } from '../side-panel/side-panel.service';
import { FeatureDetectionService } from '../utils/helpers/feature-detection.service';

SwiperCore.use([Navigation, Scrollbar, Lazy]);

@Component({
    selector: 'app-new-cars-card-list',
    template: `
        <app-component-loader *ngIf="!dataIsReady"></app-component-loader>

        <div class="new-cars-card-list" *ngIf="modelsToShow.length">
            <app-section-header
                [headerTextPrimary]="
                    headerTextCustom ? headerTextCustom : 'car_data.vehicle_models_overview_header' | translate: { vehicleMake: makeName }
                "
                [headerSize]="headerSize"
                [buttonLink]="headerLink">
            </app-section-header>

            <!-- Model Filter -->
            <ul class="new-cars-card-list__filter" role="nav" *ngIf="vehicleModelFilters && vehicleModelFilters.length">
                <li class="new-cars-card-list__filter-item" *ngFor="let filter of vehicleModelFilters; let i = index;">
                    <button (click)="activeFilter.id !== filter.id ? setModelFilter(i) : false;"
                        [ngClass]="{ 'active-item': activeFilter.id === filter.id }"
                        class="button--minimal new-cars-card-list__filter-button">
                        {{filter.name}}
                    </button>
                </li>
            </ul>

            <swiper
                class="new-cars-list__swiper"
                *ngIf="
                    isBrowser && layoutMode === 'Slider' && sliderDataReady;
                    else gridView
                "
                [lazy]="lazyOptions"
                [navigation]="showNavigation"
                [preloadImages]="false"
                [scrollbar]="{ draggable: true }"
                [slidesPerGroup]="slidesGroupAmount"
                [slidesPerView]="slidesAmount"
                [spaceBetween]="slidesSpace"
                (swiper)="onSwiper($event)"
            >
                <ng-container *ngFor="let item of modelsToShow">
                    <ng-template swiperSlide>
                        <app-new-cars-model-card
                            [data]="item"
                            [isInSlider]="true"
                        ></app-new-cars-model-card>
                    </ng-template>
                </ng-container>
            </swiper>

            <ng-template #gridView>
                <ul class="new-cars-card-list__grid">
                    <li
                        class="new-cars-card-list__grid-item"
                        *ngFor="let item of modelsToShow"
                    >
                        <app-new-cars-model-card
                            [data]="item"
                        ></app-new-cars-model-card>
                    </li>
                </ul>
            </ng-template>
        </div>
    `
})

export class NewCarsCardListComponent implements OnInit, OnDestroy {
    @Input() vehiclePageId: string | undefined;
    @Input() excludeModel?: Cms.VehicleModel;
    @Input() layoutMode: 'Grid' | 'Slider' = 'Grid';
    @Input() headerTextCustom?: string;
    @Input() headerLink?: Cms.Link;
    @Input() headerSize?: number = 1;

    makeName: string;
    vehicleModelsData: Cms.VehicleCardDto[];
    vehicleModelFilters: Cms.VehicleTypeDto[] = [];
    activeFilter: Cms.VehicleTypeDto;
    defaultFilter: Cms.VehicleTypeDto;
    defaultFilterTranslation: string;
    modelsToShow: Cms.VehicleCardDto[] = [];
    pageTypesEnum = PAGE_TYPES;
    isBrowser: boolean;

    // Slider
    slidesAmount: number;
    slidesGroupAmount: number;
    slidesSpace: number;
    showNavigation: boolean;
    sliderDataReady = false;
    dataIsReady = false;
    queries$: Observable<BreakpointState>;
    lazyOptions: LazyOptions = {
        loadOnTransitionStart: true,
        checkInView: true,
        loadPrevNext: false
    };

    private unsubscribe = new Subject<void>();

    constructor(
        private breakpointObserver: BreakpointObserver,
        private changeDetectorRef: ChangeDetectorRef,
        private contentService: ContentService,
        private featureDetectionService: FeatureDetectionService,
        private sidepanelService: SidePanelService,
        private translateService: TranslateService,
    ) {
        this.isBrowser = this.featureDetectionService.isBrowser();
    }

    ngOnInit() {
        // Get vehicle model data
        if (this.vehiclePageId) {
            this.contentService.getVehicleModelCardList(this.vehiclePageId)
            .pipe(
                catchError((error) => {
                    console.error('Error fetching vehicle card list data:', error);
                    this.dataIsReady = true;
                    return of(null);
                }),
                takeUntil(this.unsubscribe),
            )
            .subscribe((res: Cms.VehicleCardsDto | null) => {
                if (!res) {
                    this.dataIsReady = true;
                    this.changeDetectorRef.markForCheck();
                    return;
                }

                this.vehicleModelsData = res.vehicleCardsList ? this.removeExcludedModelFromList(cloneObjectArray(res.vehicleCardsList)) : [];

                this.modelsToShow = this.vehicleModelsData;
                this.makeName = res.makeName;

                this.defaultFilter = {
                    name: this.translateService.instant('car_data.vehicle_type_filter_button_all'),
                    id: 'default',
                };

                this.modelsToShow.map((model: Cms.VehicleCardDto) => {
                    if (model && model.vehicleTypes) {
                        model.vehicleTypes.map((type: Cms.VehicleTypeDto) => {
                            this.vehicleModelFilters.push(type);
                        });
                    }
                });

                if (this.vehicleModelFilters.length > 1) {
                    this.vehicleModelFilters = removeDuplicatesById(this.vehicleModelFilters);

                    if (this.vehicleModelFilters.length > 1) {
                        this.vehicleModelFilters.unshift(this.defaultFilter);
                        this.setModelFilter(0);
                    } else {
                        this.vehicleModelFilters = [];
                    }
                } else {
                    this.vehicleModelFilters = [];
                }

                this.dataIsReady = true;
                this.changeDetectorRef.markForCheck();

                if (this.layoutMode === 'Slider') {
                    this.initSlider();
                }
            });
        } else {
            this.dataIsReady = true;
            this.changeDetectorRef.markForCheck();
        }
    }

    initSlider() {
        // Detect layout changes
        this.queries$ = this.breakpointObserver.observe([
            '(min-width: 460px)',
            '(min-width: 600px)',
            '(min-width: 768px)',
            '(min-width: 1024px)',
            '(min-width: 1200px)',
            '(min-width: 1580px)'
        ]);

        this.queries$
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((result: any) => {

                if (!result.matches) {
                    this.setSlidesAmount(1);
                    this.slidesSpace = 16;
                } else {
                    if (result.breakpoints['(min-width: 1580px)'] === true) {
                        this.setSlidesAmount(3);
                        this.slidesSpace = 28;
                    } else if (result.breakpoints['(min-width: 1200px)'] === true) {
                        this.setSlidesAmount(3);
                        this.slidesSpace = 16;
                    } else if (result.breakpoints['(min-width: 1024px)'] === true) {
                        this.setSlidesAmount(3);
                        this.slidesSpace = 16;
                    } else if (result.breakpoints['(min-width: 768px)'] === true) {
                        this.setSlidesAmount(2);
                        this.slidesSpace = 16;
                    } else if (result.breakpoints['(min-width: 600px)'] === true) {
                        this.setSlidesAmount(2);
                        this.slidesSpace = 16;
                    } else if (result.breakpoints['(min-width: 460px)'] === true) {
                        this.setSlidesAmount(1);
                        this.slidesSpace = 16;
                    }
                }

                this.sliderDataReady = true;
                this.changeDetectorRef.markForCheck();
            });
    }

    setSlidesAmount(amount: number) {
        if (this.modelsToShow?.length > amount) {
            this.slidesAmount = amount + .1;
            this.showNavigation = true;
        } else {
            this.slidesAmount = amount;
            this.showNavigation = false;
        }

        this.slidesGroupAmount = amount;
    }

    // Model filter change
    setModelFilter(filterIndex: number): void {
        this.activeFilter = this.vehicleModelFilters[filterIndex];

        if (this.activeFilter.id === this.defaultFilter.id) {
            // Show all models
            this.modelsToShow = this.vehicleModelsData;
        } else {
            // Filter models based on activeFilter
            this.modelsToShow = [];

            this.modelsToShow = this.vehicleModelsData.filter((model) => {
                return model.vehicleTypes?.some(type => type.id === this.activeFilter.id);
            });
        }

        this.sliderDataReady = false;

        if (this.layoutMode === 'Slider') {
            this.initSlider();
            this.changeDetectorRef.detectChanges();
        }
    }

    removeExcludedModelFromList(models: Cms.VehicleCardDto[]): Cms.VehicleCardDto[] {
        if (this.excludeModel) {
            models = models.filter(item => {
                return item?.id !== this.excludeModel?.id && item?.modelName !== this.excludeModel?.modelName;
            });
        }

        return models;
    }

    showLoanSimulator(price: number): void {
        if (price) {
            this.sidepanelService.showLoanCalculatorSidePanel({
                objectPrice: price,
            });
        }
    }

    onSwiper(swiper: Swiper) {
        if (!swiper) {
            console.error('[BN error: Swiper not loaded');
        }
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
